/**
 * BootShell — the React half of the admin's first-paint shell.
 *
 * WHAT IT IS
 * ----------
 * `index.html` ships this exact same markup, statically, INSIDE `<div id="root">`, so the
 * browser paints a real page shape before a single byte of app JS has been parsed. When
 * React finally mounts, `createRoot(container).render()` CLEARS the container's existing
 * children on its first commit — verified empirically against this app's own vendor-react
 * chunk in production:
 *
 *     BEFORE=<p id="pre-existing-shell">SHELL</p> || AFTER="" || shellStillPresent=false
 *
 * That swap is ATOMIC, inside one commit. So there is deliberately NO teardown code
 * anywhere for the static shell — no requestAnimationFrame, no timeout fail-safe, no
 * removal call. React does it, in the same frame it paints the replacement.
 *
 * This component is what that replacement draws while the boot gates (router → auth →
 * shop → route chunk → campaigns query) are still resolving. Because it emits the SAME
 * class names as the static shell and both are styled by the ONE inline stylesheet in the
 * document head — React clears `#root`, it never touches `<head>` — the handoff is
 * pixel-identical and the merchant sees one unchanging picture from first paint until the
 * real dashboard replaces it.
 *
 * ⚠️ NO POLARIS IMPORTS, deliberately. The whole point is to render before (and without)
 * the 437 kB Polaris component stylesheet and the `s-*` custom-element registry. Plain
 * divs + the head stylesheet, nothing else. Adding an import here would put this component
 * in the critical path of the thing it exists to cover for. (`utils/adminLoading` is the
 * one allowed import: it pulls in nothing but `useEffect`, which React has already parsed
 * by the time this renders.)
 *
 * ⚠️ DUPLICATED SHAPES. This file and the static shell in `index.html` draw the same
 * picture, and `DashboardSkeleton.jsx` is the shape's source of truth. Drift is a visible
 * jump. `scripts/verify-boot-cover.mjs` enforces all three mechanically and runs in
 * `yarn test`; if you change a bar count or a class name here, change it in `index.html`
 * too or the build fails.
 */
import { useAdminLoading } from "../utils/adminLoading";

/** One run of grey body-text bars. `lines` mirrors `<SkeletonBodyText lines={n} />`. */
function Body({ lines }) {
  return (
    <div className="ns-boot-body" data-lines={lines}>
      {Array.from({ length: lines }, (_, i) => (
        <div key={i} className="ns-boot-line" />
      ))}
    </div>
  );
}

/** One grey display bar. Mirrors `<SkeletonDisplayText size="small" />`. */
function Display() {
  return <div className="ns-boot-display" />;
}

/**
 * Which shell a given path should draw.
 *
 *   "/"                              → dashboard shell (mirrors DashboardSkeleton.jsx)
 *   "/api/shopify/install-or-render" → dashboard shell. SEE THE WARNING BELOW.
 *   "/popup"   → NOTHING. The campaign editor opens as an App Bridge <Modal src="/popup">,
 *                i.e. its own iframe/JS realm with its own CampaignEditorSkeleton. A page
 *                shell there would be a second, wrong picture in front of that one.
 *   "/welcome" → NOTHING. The App Pricing return landing has PlanActivationScreen.
 *   else       → generic shell (heading + one wide card)
 *
 * ⚠️ The `/api/shopify/install-or-render` branch is DEFENCE IN DEPTH, not a live fix — do not
 * delete it, but do not believe it is load-bearing today either.
 *
 * ⚠️ UPDATED 2026-08-18: `application_url` is now the ROOT path in both TOMLs, so the embedded
 * iframe points straight at "/" and this branch is no longer reachable on a normal boot.
 * It was never load-bearing even when it was reachable — that path answered **302 → Location: /**,
 * so the document that actually parsed was always at "/" and the plain `p === "/"` test already
 * selected the dashboard variant. Measured before the change:
 *     document.querySelector('iframe').src → https://app.nudgesmart.com/api/shopify/install-or-render
 *     curl -D- ".../api/shopify/install-or-render?shop=…&embedded=1"  → HTTP/2 302, location: /
 *     in the loaded frame: location.pathname === "/", html.className === "ns-r-dashboard"
 * Keep the branch: it costs one string compare and it covers a merchant whose browser or
 * Shopify still holds the old URL, plus the case where Gadget ever serves the SPA at that path
 * directly instead of redirecting — at which point every merchant would silently get the
 * generic shell. A prefix test costs nothing and fails safe toward the common case.
 *
 * The trailing slash is normalised, except on the root — "/" must stay "/".
 * `index.html`'s inline route script implements this identically; keep them in step.
 * `scripts/verify-boot-cover.mjs` does NOT guard this rule — it compares drawn shapes, not
 * route logic — so the two copies are held together by this comment and nothing else.
 */
const INSTALL_OR_RENDER = "/api/shopify/install-or-render";

export function bootShellVariant(pathname) {
  let p = typeof pathname === "string" && pathname ? pathname : "/";
  if (p.length > 1 && p.charAt(p.length - 1) === "/") p = p.slice(0, -1);
  if (p === "/" || p.startsWith(INSTALL_OR_RENDER)) return "dashboard";
  if (p === "/popup" || p === "/welcome") return null;
  return "generic";
}

/**
 * Boot-time variant for gates that render ABOVE <RouterProvider>, where there is no
 * router context and `useLocation()` would throw. Read from window.location at module
 * scope on purpose: those gates only ever run during the initial document load, so the
 * landing path is a fact about this page load, not state that can change.
 */
export const BOOT_SHELL_VARIANT =
  typeof window !== "undefined" ? bootShellVariant(window.location.pathname) : null;

/**
 * @param {{ variant?: "dashboard" | "generic" | null }} props
 * Any other value renders nothing — that is the "/popup" and "/welcome" case, and it lets
 * every call site write `<BootShell variant={bootShellVariant(path)} />` without its own
 * null branch.
 */
export default function BootShell({ variant = "generic" }) {
  // Also drive Shopify's own header progress bar, outside our iframe — the same
  // affordance the admin and first-party apps (Flow) use. See utils/adminLoading.js.
  // Unconditional and above every return, including the null one: a `variant === null`
  // BootShell is still a boot gate, it just lets another component draw the picture.
  useAdminLoading();

  if (variant === "dashboard") {
    /* ns-boot:dashboard */
    return (
      <div className="ns-boot ns-boot--dashboard">
        {/*
          Real text, not a grey bar — see the .ns-boot-title comment in index.html.
          FCP needs text/image/SVG; a skeleton of background-coloured divs paints for the
          merchant but records nothing. That is a spec fact — the `paint` entries === []
          measurement originally cited here was INVALID (background tab; Chrome suppresses
          paint timing when the document was never visible). See the retraction in
          docs/architecture/admin-load-time.md. This must stay byte-identical to
          index.html's dashboard branch and to DashboardSkeleton.jsx's <s-page heading>.
        */}
        <div className="ns-boot-title">Nudgesmart Dashboard</div>
        <div className="ns-boot-grid">
          {/* Main column (2fr) — analytics overview, then active campaigns. */}
          <div className="ns-boot-col">
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
                <Display />
              </div>
              <div className="ns-boot-metrics">
                <Body lines={2} />
                <Body lines={2} />
                <Body lines={2} />
                <Body lines={2} />
              </div>
            </div>
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
                <Display />
              </div>
              <Body lines={1} />
              <Body lines={4} />
            </div>
          </div>
          {/* Sidebar (1fr) — what's new, support, summary. */}
          <div className="ns-boot-col">
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
              </div>
              <Body lines={3} />
            </div>
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
              </div>
              <Body lines={2} />
            </div>
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
              </div>
              <Body lines={3} />
            </div>
          </div>
        </div>
      </div>
    );
    /* /ns-boot:dashboard */
  }

  if (variant === "generic") {
    /* ns-boot:generic */
    return (
      <div className="ns-boot ns-boot--generic">
        <div className="ns-boot-heading" />
        <div className="ns-boot-grid">
          <div className="ns-boot-col">
            <div className="ns-boot-card">
              <div className="ns-boot-cardhead">
                <Display />
              </div>
              <Body lines={3} />
            </div>
          </div>
        </div>
      </div>
    );
    /* /ns-boot:generic */
  }

  return null;
}
